import { useEffect, useState, type ReactNode } from "react";

/**
 * Mounts children only after first user interaction OR after the browser is idle.
 * Used to defer non-critical UI (Toaster, Sonner) so they don't compete with LCP.
 *
 * `minDelayMs` enforces a minimum wait before mounting even if idle/interaction
 * fires earlier — useful for very-low-priority widgets (e.g. chat launcher) that
 * should never appear during the LCP/Speed-Index measurement window.
 */
export function DeferredMount({
  children,
  minDelayMs = 0,
}: {
  children: ReactNode;
  minDelayMs?: number;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const startedAt = performance.now();
    const trigger = () => {
      if (cancelled) return;
      const elapsed = performance.now() - startedAt;
      if (elapsed < minDelayMs) {
        window.setTimeout(trigger, minDelayMs - elapsed);
        return;
      }
      cancelled = true;
      setReady(true);
      cleanup();
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, trigger));
    };
    events.forEach((e) => window.addEventListener(e, trigger, { once: true, passive: true } as AddEventListenerOptions));

    const ric: any = (window as any).requestIdleCallback;
    const idle = ric
      ? ric(trigger, { timeout: Math.max(2500, minDelayMs + 500) })
      : window.setTimeout(trigger, Math.max(2000, minDelayMs));

    return () => {
      cancelled = true;
      cleanup();
      if (ric && (window as any).cancelIdleCallback) (window as any).cancelIdleCallback(idle);
      else window.clearTimeout(idle as number);
    };
  }, [minDelayMs]);

  return ready ? <>{children}</> : null;
}
